import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Card from "../../shared/Card/Card";
import ContactFromId from "../../contacts/ContactFromId";
import Modal from "../../../layouts/Modal";
import LocationPicker from "../LocationPicker";
import PartNumberEdit from "../PartNumberEdit";
import { EditDimensions } from "../shipping/EditDimensions";
import PartGradeBadge from "../../shared/PartGradeBadge";
import PartCategoryCombo from "../../leads/LeadParts/PartCategoryCombo";
import EbayResearchButton from "./EbayResearchButton";
import SupersededPartNumbersEditor from "./SupersededPartNumbersEditor";
import UnlistedOfferPrice from "../../ebayListings/unlistedTable/UnlistedOfferPrice";
import WarehouseLocationFromId from "../../warehouseLocations/WarehouseLocationFromId";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import useContactSingle from "../../../data/useContactsSingle";
import usePartMutations from "../../../data/usePartMutations";
import useWilmaUsers from "../../../data/useWilmaUsers";
import useNotification from "../../notifications/useNotifications";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import { IPart, IPartCategory, PartStatus } from "../../../models/Part";
import Field from "./StockField";
import StockStatusEditor from "./StockStatusEditor";

const formatMins = (mins: number | null | undefined) =>
  mins ? `${mins} mins` : "-";

const formatViews = (views: number | null | undefined) =>
  views === null || views === undefined ? "-" : views.toLocaleString();

const daysSince = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return null;
  const diff = Math.floor(DateTime.now().diff(dt, "days").days);
  return diff < 0 ? 0 : diff;
};

function MarketplaceRow({
  name,
  itemNumber,
  publishedOn,
  listedOn,
  views,
}: {
  name: string;
  itemNumber: string | null | undefined;
  publishedOn: string | null | undefined;
  listedOn: string | null | undefined;
  views: number | null | undefined;
}) {
  const days = daysSince(listedOn);
  const isListed = !!itemNumber || !!publishedOn || !!listedOn;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{name}</h4>
        <span
          className={
            isListed
              ? "inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 ring-1 ring-inset ring-green-200"
              : "inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-inset ring-gray-200"
          }
        >
          <span
            className={
              "h-1.5 w-1.5 rounded-full " +
              (isListed ? "bg-green-500" : "bg-gray-400")
            }
          />
          {isListed ? "Listed" : "Not Listed"}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="Item #">{itemNumber ?? "-"}</Field>
        <Field label="Published">
          {formatDateTime(publishedOn, DateTime.DATETIME_SHORT, "Not Published")}
        </Field>
        <Field label={`Days on ${name}`}>
          {days === null ? "-" : `${days} ${days === 1 ? "day" : "days"}`}
        </Field>
        <Field label="Views">{formatViews(views)}</Field>
      </div>
    </div>
  );
}

type StatTone = "emerald" | "sky" | "indigo" | "slate" | "amber" | "red";

const STAT_TONE: Record<StatTone, { bg: string; ring: string; text: string; dot: string }> = {
  emerald: { bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  sky: { bg: "bg-sky-50", ring: "ring-sky-200", text: "text-sky-800", dot: "bg-sky-500" },
  indigo: { bg: "bg-indigo-50", ring: "ring-indigo-200", text: "text-indigo-800", dot: "bg-indigo-500" },
  slate: { bg: "bg-slate-50", ring: "ring-slate-200", text: "text-slate-800", dot: "bg-slate-500" },
  amber: { bg: "bg-amber-50", ring: "ring-amber-200", text: "text-amber-800", dot: "bg-amber-500" },
  red: { bg: "bg-red-50", ring: "ring-red-200", text: "text-red-800", dot: "bg-red-500" },
};

function SimilarSoldStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  tone: StatTone;
}) {
  const t = STAT_TONE[tone];
  const hasValue = typeof value === "number";
  return (
    <div
      className={`rounded-md ${t.bg} ${t.text} ring-1 ring-inset ${t.ring} px-3 py-2`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
        <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
        {label}
      </div>
      <div className="mt-0.5 text-xl font-semibold leading-tight">
        {hasValue ? value!.toLocaleString() : "-"}
      </div>
      <div className="text-[11px] opacity-75">
        {hasValue
          ? `similar part${value === 1 ? "" : "s"} sold`
          : "No data yet"}
      </div>
    </div>
  );
}

function SellThroughStat({ value }: { value: number | null | undefined }) {
  const hasValue = typeof value === "number";
  const pct = hasValue ? Math.max(0, Math.min(100, value!)) : 0;
  const tone: StatTone = !hasValue
    ? "slate"
    : pct >= 70
      ? "emerald"
      : pct >= 40
        ? "sky"
        : pct >= 20
          ? "amber"
          : "red";
  const t = STAT_TONE[tone];
  return (
    <div
      className={`rounded-md ${t.bg} ${t.text} ring-1 ring-inset ${t.ring} px-3 py-2`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
        <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
        Category Sell-Through
      </div>
      <div className="mt-0.5 text-xl font-semibold leading-tight">
        {hasValue ? `${pct.toFixed(0)}%` : "-"}
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/60">
        <div
          className={`h-full ${t.dot}`}
          style={{ width: `${pct}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

export default function StockOverview({ part }: { part: IPart }) {
  const { wilmaUsers } = useWilmaUsers();
  const { contact } = useContactSingle(part.removedBy!);

  const [locationOpen, setLocationOpen] = useState(false);
  const [partNumberOpen, setPartNumberOpen] = useState(false);
  const [dimensionsOpen, setDimensionsOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<IPartCategory | null>(
    null
  );
  const [locatedBy, setLocatedBy] = useState<string | null>(null);

  const { patchPart } = usePartMutations();
  const { addNotification } = useNotification();

  const handleSaveCategory = () => {
    if (!pendingCategory) return;
    if (pendingCategory.id === part.categoryId) {
      setCategoryOpen(false);
      setPendingCategory(null);
      return;
    }
    patchPart.mutate(
      {
        id: part.id,
        categoryId: pendingCategory.id,
        vehicleId: part.vehicleId,
      },
      {
        onSuccess: () => {
          addNotification({
            primaryText: "Category updated",
            variant: "success",
          });
          setCategoryOpen(false);
          setPendingCategory(null);
        },
        onError: () => {
          addNotification({
            primaryText: "Failed to update category",
            variant: "error",
          });
        },
      }
    );
  };

  useEffect(() => {
    if (part.locatedBy && !wilmaUsers.isLoading && wilmaUsers.data) {
      const user = wilmaUsers.data.data.find((u) => u.id === part.locatedBy);
      setLocatedBy(user ? user.fullname : "Unknown User");
    }
  }, [part.locatedBy, wilmaUsers.data, wilmaUsers.isLoading]);

  const hasFulfillment = useMemo(
    () =>
      part.soldDate ||
      part.pickedOn ||
      part.shippedOn ||
      part.trackingNumber ||
      part.orderId,
    [part]
  );

  // Estimated storage cost = days in stock × per-day storage rate.
  // The category's `whCost` field is treated as the per-part-per-day storage rate.
  // Storage stops accruing once the part is sold (uses sold date as the end).
  const storageEstimate = useMemo(() => {
    const rate = part.category?.whCost;
    if (rate == null || rate <= 0) return null;
    const startIso = part.removedOn ?? part.created;
    if (!startIso) return null;
    const start = DateTime.fromISO(startIso);
    if (!start.isValid) return null;
    const endIso = part.soldDate ?? part.shippedOn ?? null;
    const end = endIso ? DateTime.fromISO(endIso) : DateTime.now();
    const days = Math.max(0, Math.floor(end.diff(start, "days").days));
    return {
      rate,
      days,
      total: days * rate,
      tooltip: `${days} day${days === 1 ? "" : "s"} since ${
        part.removedOn ? "dismantled" : "created"
      } at ${PriceFormat(rate)} / day (warehouse cost from part category)`,
    };
  }, [part.category?.whCost, part.removedOn, part.created, part.soldDate, part.shippedOn]);

  const hasQc =
    part.partStatus === PartStatus.QC || part.qcReason || part.qcApprovedComment;
  const hasDismantle = !!part.removedOn || !!part.removedBy;

  const dimensionsLabel = useMemo(() => {
    const fmt = (v: number | null | undefined) =>
      v == null ? "?" : `${v}`;
    if (
      part.length == null &&
      part.width == null &&
      part.height == null
    ) {
      return "-";
    }
    return `${fmt(part.length)} × ${fmt(part.width)} × ${fmt(part.height)} cm`;
  }, [part.length, part.width, part.height]);

  const weightLabel =
    part.weight == null ? "-" : `${part.weight} kg`;

  return (
    <div className="flex flex-col gap-4">
      <LocationPicker open={locationOpen} setOpen={setLocationOpen} part={part} />
      <PartNumberEdit
        open={partNumberOpen}
        setOpen={setPartNumberOpen}
        part={part}
      />
      <Modal open={dimensionsOpen} setOpen={setDimensionsOpen} width="max-w-2xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Edit Dimensions
        </h3>
        <EditDimensions
          dimensions={{
            length: part.length ?? 0,
            width: part.width ?? 0,
            height: part.height ?? 0,
            weight: part.weight ?? 0,
          }}
          partId={part.id}
          categoryId={part.categoryId ?? undefined}
          onEditSuccess={() => setDimensionsOpen(false)}
        />
      </Modal>
      <Modal
        open={categoryOpen}
        setOpen={(v) => {
          setCategoryOpen(v);
          if (!v) setPendingCategory(null);
        }}
        width="max-w-2xl"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Change Category
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Current:{" "}
          <span className="font-medium text-gray-700">
            {part.category?.name ?? "—"}
          </span>
        </p>
        <PartCategoryCombo
          handleAddCategory={(c: IPartCategory) => setPendingCategory(c)}
          placeholder={
            pendingCategory?.name ?? "Start typing to search for a category"
          }
        />
        {pendingCategory && (
          <div className="mt-3 rounded-md border border-primary-100 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            Selected:{" "}
            <span className="font-semibold">{pendingCategory.name}</span>
          </div>
        )}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setCategoryOpen(false);
              setPendingCategory(null);
            }}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            Cancel
          </button>
          <ButtonInput
            label="Save Category"
            isSubmit={false}
            classes=""
            isLoading={patchPart.isLoading}
            disabled={!pendingCategory}
            onClick={handleSaveCategory}
          />
        </div>
      </Modal>

      {/* Identification */}
      <Card title="Identification" bodyClassName="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Tag Number">{part.tagNumber ?? "Not Found"}</Field>
          <Field
            label="Part Number"
            action={
              <FontAwesomeIcon
                className="rounded-full cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setPartNumberOpen(true)}
              />
            }
          >
            {part.partNumber ?? "Not Found"}
          </Field>
          <Field label="OE Part Number">{part.oePartNumber ?? "Not Found"}</Field>
          <Field
            label="Category"
            action={
              <FontAwesomeIcon
                className="rounded-full cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setCategoryOpen(true)}
              />
            }
          >
            {part.category?.name ?? "-"}
          </Field>
          <Field label="Grade">
            <PartGradeBadge part={part} />
          </Field>
          <Field label="Part Status">
            <StockStatusEditor part={part} />
          </Field>
          <Field label="Date Created">
            {formatDateTime(part.created, DateTime.DATETIME_SHORT, "-")}
          </Field>
          <Field
            label="Dimensions (L × W × H)"
            action={
              <FontAwesomeIcon
                className="rounded-full cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setDimensionsOpen(true)}
              />
            }
          >
            {dimensionsLabel}
          </Field>
          <Field
            label="Weight"
            action={
              <FontAwesomeIcon
                className="rounded-full cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setDimensionsOpen(true)}
              />
            }
          >
            {weightLabel}
          </Field>
        </div>

        <div className="mt-4 border-t border-gray-100 pt-4">
          <SupersededPartNumbersEditor part={part} />
        </div>
      </Card>

      {/* Location & Handling */}
      <Card title="Location & Handling" bodyClassName="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field
            label="Location"
            action={
              <FontAwesomeIcon
                className="rounded-full cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setLocationOpen(true)}
              />
            }
          >
            <WarehouseLocationFromId id={part.warehouseLocationId} />
          </Field>
          <Field label="Located By">{locatedBy ?? "-"}</Field>
          <Field label="Last Stock Take">
            {formatDateTime(
              part.lastStockChecked,
              DateTime.DATETIME_SHORT,
              "Never"
            )}
          </Field>
          <Field label="Est. Storage Cost">
            {storageEstimate ? (
              <span title={storageEstimate.tooltip}>
                <span className="font-medium text-gray-900">
                  {PriceFormat(storageEstimate.total)}
                </span>{" "}
                <span className="text-xs text-gray-500">
                  ({storageEstimate.days}d × {PriceFormat(storageEstimate.rate)}/day)
                </span>
              </span>
            ) : (
              "-"
            )}
          </Field>
          <Field label="Est. Picking">{formatMins(part.category?.pickingTime)}</Field>
          <Field label="Est. Packing">{formatMins(part.category?.packingTime)}</Field>
        </div>
      </Card>

      {/* Pricing */}
      <Card
        title="Pricing"
        bodyClassName="p-4"
        headerRight={<EbayResearchButton part={part} />}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field
            label="Price"
            action={<EbayResearchButton part={part} iconOnly size="xs" />}
          >
            {part.partStatus === PartStatus.Sold && part.price ? (
              <span className="text-sm font-semibold text-gray-900">
                {PriceFormat(part.price)}
              </span>
            ) : (
              <div className="-ml-1">
                <UnlistedOfferPrice
                  part={part}
                  showButtons={false}
                  hideLabel
                  compact
                />
              </div>
            )}
          </Field>
          <Field label="RRP">
            {part.rrp ? (
              <span className="inline-flex items-baseline gap-1.5">
                <span>{PriceFormat(part.rrp)}</span>
                {typeof part.price === "number" &&
                  part.price > 0 &&
                  part.rrp > part.price && (
                    <span className="text-[11px] font-medium text-emerald-600">
                      -{(((part.rrp - part.price) / part.rrp) * 100).toFixed(0)}%
                    </span>
                  )}
              </span>
            ) : (
              "-"
            )}
          </Field>
          <Field label="Suggested Price">
            {part.suggestedPrice ? PriceFormat(part.suggestedPrice) : "-"}
          </Field>
          <Field label="Market Data Price">
            {part.marketDataPrice ? PriceFormat(part.marketDataPrice) : "-"}
          </Field>
          <Field label="Last Sold Price">
            {part.lastSoldPrice ? PriceFormat(part.lastSoldPrice) : "-"}
          </Field>
          <Field label="Priced By">
            {part.pricedBy ? <ContactFromId contactId={part.pricedBy} /> : "-"}
          </Field>
          <Field label="Priced On">
            {formatDateTime(part.pricedOn, DateTime.DATETIME_SHORT, "-")}
          </Field>
          <Field label="Margin">
            {part.margin !== null && part.margin !== undefined
              ? `${part.margin.toFixed(2)}%`
              : "-"}
          </Field>
        </div>

        {/* Demand signals */}
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Demand
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SimilarSoldStat
              label="Sold (30d)"
              value={part.similarSoldLast30}
              tone="emerald"
            />
            <SimilarSoldStat
              label="Sold (60d)"
              value={part.similarSoldLast60}
              tone="sky"
            />
            <SimilarSoldStat
              label="Sold (90d)"
              value={part.similarSoldLast90}
              tone="indigo"
            />
            <SellThroughStat value={part.categorySellThroughRate} />
          </div>
          <p className="mt-2 text-[11px] text-gray-400">
            Similar-part sales in the same category{part.grade ? ` and grade ${part.grade}` : ""}
            {" · "}
            category sell-through = sold ÷ listed in the last 90 days.
          </p>
        </div>
      </Card>

      {/* Marketplaces */}
      <Card title="Marketplaces" bodyClassName="p-4">
        <div className="flex flex-col gap-4">
          <MarketplaceRow
            name="eBay"
            itemNumber={part.ebayItemNumber}
            publishedOn={part.offers[0]?.publishedOn ?? null}
            listedOn={part.listedDate ?? part.offers[0]?.listingStarted ?? null}
            views={part.ebayViews ?? null}
          />
          <MarketplaceRow
            name="Shopify"
            itemNumber={part.shopifyItemNumber ?? null}
            publishedOn={part.shopifyPublishedOn ?? null}
            listedOn={part.shopifyPublishedOn ?? null}
            views={part.shopifyViews ?? null}
          />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
            <Field label="Sold">
              {formatDateTime(part.soldDate, DateTime.DATETIME_SHORT, "Not Sold")}
            </Field>
          </div>
        </div>
      </Card>

      {/* Fulfillment */}
      {hasFulfillment && (
        <Card title="Fulfillment" bodyClassName="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Picked">
              {formatDateTime(part.pickedOn, DateTime.DATETIME_SHORT, "Not Picked")}
            </Field>
            <Field label="Shipped">
              {formatDateTime(part.shippedOn, DateTime.DATETIME_SHORT, "Not Shipped")}
            </Field>
            <Field label="Tracking">
              {part.trackingNumber ?? "-"}
            </Field>
            {(part.length || part.width || part.height || part.weight) && (
              <Field label="Dimensions">
                {part.length ?? "-"} × {part.width ?? "-"} × {part.height ?? "-"} cm
                {part.weight ? ` · ${part.weight}kg` : ""}
              </Field>
            )}
          </div>
        </Card>
      )}

      {/* QC */}
      {hasQc && (
        <Card title="QC" bodyClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Status Message">{part.statusMessage ?? "-"}</Field>
            <Field label="QC Reason">
              <span className="whitespace-pre-line">{part.qcReason ?? "-"}</span>
            </Field>
            <Field label="QC Comment">
              <span className="whitespace-pre-line">{part.qcApprovedComment ?? "-"}</span>
            </Field>
            <Field label="Passed QC">
              {formatDateTime(part.passedQcOn, DateTime.DATETIME_SHORT, "-")}
            </Field>
          </div>
        </Card>
      )}

      {/* Dismantle */}
      {hasDismantle && (
        <Card title="Dismantle" bodyClassName="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Dismantled On">
              {formatDateTime(part.removedOn, DateTime.DATETIME_SHORT, "Not Dismantled")}
            </Field>
            <Field label="Dismantled By">
              {part.removedBy ? contact.data?.data?.fullname ?? "-" : "Not Dismantled"}
            </Field>
            <Field label="Imaged On">
              {formatDateTime(part.imagedOn, DateTime.DATETIME_SHORT, "-")}
            </Field>
          </div>
        </Card>
      )}
    </div>
  );
}
